import { useCallback, useMemo, useState } from 'react';

import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { useHotelSpacesTypes } from 'app/hooks/data/useHotelSpacesTypes';
import type { SpaceContact } from 'app/typings/core/common/contacts/contacts';

import { buildSpaceOptions } from '../columns';

import type { CoverageExperience, SpaceContactSummary } from './summaries';
import {
  buildSpacesContactsSummaries,
  getNewSpacesContacts,
} from './summaries';

type SpacesContactsValues = { spacesContacts?: SpaceContact[] };

type Params<TValues extends SpacesContactsValues> = {
  hotelId?: number;
  experiences: CoverageExperience[];
  initialSpacesContacts: SpaceContact[] | undefined;
  refetchExperiences: () => Promise<unknown>;
  onConfirmedSave: (values: TValues) => void | Promise<unknown>;
};

export const useSpacesContactsSaveModal = <
  TValues extends SpacesContactsValues,
>({
  hotelId,
  experiences,
  initialSpacesContacts,
  refetchExperiences,
  onConfirmedSave,
}: Params<TValues>) => {
  const { data: hotel } = useGetHotel(hotelId);
  const { data: spaceTypes } = useHotelSpacesTypes();
  const { data: categories } = useExperiencesCategories();

  const [pendingValues, setPendingValues] = useState<TValues | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const spaceLabelById = useMemo(
    () =>
      buildSpaceOptions(hotel?.spaces ?? [], spaceTypes ?? []).reduce<
        Record<number, string>
      >((acc, option) => {
        acc[option.value] = option.label;

        return acc;
      }, {}),
    [hotel?.spaces, spaceTypes]
  );

  const categoryNameById = useMemo(
    () =>
      (categories ?? []).reduce<Record<number, string>>((acc, category) => {
        acc[category.id] = category.name;

        return acc;
      }, {}),
    [categories]
  );

  const summaries = useMemo<SpaceContactSummary[]>(() => {
    if (!pendingValues) {
      return [];
    }

    return buildSpacesContactsSummaries({
      spacesContacts: pendingValues.spacesContacts ?? [],
      initialSpacesContacts: initialSpacesContacts ?? [],
      experiences,
      spaceLabelById,
      categoryNameById,
    });
  }, [
    pendingValues,
    initialSpacesContacts,
    experiences,
    spaceLabelById,
    categoryNameById,
  ]);

  const handleFinish = useCallback(
    async (values: TValues) => {
      if (pendingValues) {
        return;
      }

      if (
        getNewSpacesContacts(initialSpacesContacts, values.spacesContacts)
          .length > 0
      ) {
        await refetchExperiences();
        setPendingValues(values);

        return;
      }

      try {
        await onConfirmedSave(values);
      } catch {
        // The mutation already surfaces its own error message.
      }
    },
    [pendingValues, initialSpacesContacts, onConfirmedSave, refetchExperiences]
  );

  const confirm = useCallback(async () => {
    if (!pendingValues) {
      return;
    }

    setIsConfirming(true);

    try {
      await onConfirmedSave(pendingValues);
      setPendingValues(null);
    } catch {
      // On failure the modal stays open so the user can retry; the mutation
      // already surfaces its own error message.
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirmedSave, pendingValues]);

  const cancel = useCallback(() => {
    setPendingValues(null);
  }, []);

  return {
    isOpen: pendingValues !== null,
    isLoading: isConfirming,
    summaries,
    handleFinish,
    confirm,
    cancel,
  };
};
